/**
 * Vision — render-free fog-of-war / visibility simulation.
 *
 * SCALE ARCHITECTURE (20,000+ units): visibility lives on one shared grid
 * per faction. Units NEVER cast their own viewsheds — each unit's vision
 * cost is a single O(1) stamp of its position into its faction's coarse
 * observer grid (`stampUnit`: one hash + three array writes). On recompute
 * (at most every 0.5s of sim time), each occupied observer cell casts ONE
 * raycast viewshed from the stamped centroid against the baked terrain
 * heightmap (+ forest density + building occluders), accumulated with max
 * into the faction's visibility grid. Cost scales with occupied clusters,
 * not units: 20k units in armies cost hundreds of viewsheds, not 20k.
 * Buildings are few and keep individual data-driven viewsheds.
 *
 * Two teams (0 = player, 1 = AI) each get a visibility grid plus a sticky
 * "explored" memory. Visibility is computed by casting rays from every
 * cluster/building viewer and marching them outward over a precomputed
 * heightfield, so hills and forests genuinely carve the visible region —
 * it is never a clean circle. The AI scouts through the faction/grid-level
 * API (`isVisible`, `isCellVisible`, `exploredGrid`, ...), which is the
 * honest no-cheating interface: it only ever reports what the team's
 * observers can see.
 *
 * Render-free: no three.js, no Math.random — only deterministic integer
 * hashing. Fixed-timestep friendly: `update()` recomputes at most every
 * 0.5s of sim time. Jitter stability: building viewers keep a stable array
 * order (caller sorts them); cluster seeds key off observer-cell
 * coordinates, so recomputes never shimmer and stamp order is irrelevant.
 *
 * Caller contract: gate stamping on `isDue(simNow)` — stamp units, then
 * call `update()`. Stamps are single-use: `update()` consumes and clears
 * them whether or not a recompute was due.
 *
 * Sight / occlusion rules (all tunable consts at the top of this file):
 * - Grid: 128x128 over a 400u world (-200..200). A 192x192 height grid is
 *   baked once in the constructor from the injected `groundY` and
 *   bilinear-sampled during LOS — the expensive noise groundY is never
 *   called per ray step.
 * - Observer grid: CLUSTER_CELL=32u cells per (team, unit kind). A lone
 *   scout's centroid IS its position (zero discretization error); a
 *   formation's centroid is its honest group position.
 * - Per viewshed: RAYS=192 rays, marched in ~2.5u steps. Eye at
 *   ground+1.7; each step's sight line runs from the eye to (step ground
 *   + 1.0).
 * - Hills: a running maximum of (terrain - eye - 0.5)/dist is tracked; a
 *   step is blocked when its sight-line slope falls below that maximum,
 *   i.e. terrain pokes more than HILL_TOLERANCE=0.5 above the sight line.
 *   Blocked steps are skipped but the march continues, so a hill shoulder
 *   hides the near slope while distant ground past the horizon stays
 *   visible (maximum-slope viewshed).
 * - Forest: density*stepLen accumulates along the ray; the ray is blocked
 *   when the accumulator reaches FOREST_BLOCK=14, and visibility is softly
 *   dimmed by (1 - accum/FOREST_BLOCK) before that. A lone timber node
 *   (gaussian sigma 5, weight 1) integrates to ~12.5 through its center —
 *   heavy dimming but no block; two overlapping nodes block.
 * - Anti-circle: (a) per-ray max-range jitter ±12% from a deterministic
 *   hash of (seed, rayIndex); (b) smoothstep falloff — full visibility to
 *   50% of max range, fading to 0 at max range (no hard ring); (c)
 *   hills/forest carve the actual shape. Max over viewsheds per team per
 *   cell.
 * - Ranges are data-driven where the UE data model provides them: every
 *   building viewer takes its day range from the building def's `vision`
 *   field (all 104 buildings define it — e.g. forum 40, domus 14,
 *   signal_brazier 52) and its eye height from `vision_height` when
 *   present (watchtowers, the brazier at 1.35). The kind tables below are
 *   fallbacks only. Night range = day range * NIGHT_RATIO (prototype
 *   day/night tuning; the data model carries a single `vision` value).
 *   Braziers are light sources (ratio 1): they ignore the night shrink.
 *   Torch range (26) is prototype-side — no UE data yet. Torches fold into
 *   their fighter's cluster viewshed: at every night factor the fighter
 *   night range (110→30) already exceeds the 26u torch bubble from the
 *   same position, so the union IS the fighter viewshed — no per-unit
 *   torch viewsheds, zero behavior change. (If torch range is ever tuned
 *   above fighter night range, the fold must be revisited.)
 *   Day/night blend is lerp by nightFactor. Elevation bonus: range *=
 *   1 + clamp((eyeY - 2) * 0.012, 0, 0.5), so a hilltop viewer sees up to
 *   50% farther than one at datum.
 * - Building occluders: active buildings whose def defines
 *   `occluder_height` (walls, watchtowers, fieldworks) rasterize their top
 *   height into an occlusion grid via setOccluders(). In the march they
 *   behave as narrow steep hills inside the maximum-slope computation, so
 *   a wall hides the ground directly behind it while a hilltop past it
 *   stays visible. A viewer's own footprint (clearRadius) never occludes
 *   its own sight; clusters clear their own cell (CLUSTER_CLEAR).
 * - Explored: visibility > 0.15 marks a cell explored (sticky until
 *   reset/restore). isVisible() = visibility > 0.25.
 */

export type VisionTeam = 0 | 1;
/** Unit kinds that stamp into the faction observer grids. */
export type UnitKind = "fighter" | "worker";
/**
 * Individual viewshed kinds. Unit clusters reuse "fighter"/"worker"
 * (stamped, never passed in directly); buildings/braziers are passed to
 * update() as individual data-driven viewers.
 */
export type ViewerKind = "fighter" | "worker" | "building" | "brazier";

export interface VisionViewer {
  x: number;
  z: number;
  team: VisionTeam;
  kind: ViewerKind;
  /**
   * Day sight range in world units, sourced from the UE data `vision`
   * field on the building def. Overrides the kind-table fallback below.
   */
  rangeDay?: number;
  /**
   * Eye height above local ground, sourced from the UE data
   * `vision_height` field. Overrides EYE_HEIGHT.
   */
  eyeHeight?: number;
  /**
   * Radius (world units) around the viewer inside which building
   * occluders are ignored — a viewer's own footprint must not blind it.
   */
  clearRadius?: number;
}

/** Rays cast per viewer. */
const RAYS = 192;
/** Ray-march step length in world units. */
const MARCH_STEP = 2.5;
/** Eye height above local ground. */
const EYE_HEIGHT = 1.7;
/** Sight lines aim at ground + this at the target cell. */
const TARGET_HEIGHT = 1.0;
/** Terrain must poke this far above the sight line to block a ray. */
const HILL_TOLERANCE = 0.5;
/** Gaussian sigma of a timber node's forest-density splat (~10u footprint). */
const FOREST_SIGMA = 5;
/** Timber splat cutoff radius (3 sigma); the density grid is exactly 0 beyond. */
const FOREST_SPLAT_RADIUS = FOREST_SIGMA * 3;
/** Accumulated density*length that fully blocks a ray. */
const FOREST_BLOCK = 14;
/** Coarse forest-presence mask resolution (exact skip test per ray step). */
const FOREST_MASK_CELLS = 32;
/** Per-ray max-range jitter, ± this fraction. */
const RANGE_JITTER = 0.12;
/** Full visibility inside this fraction of (jittered) max range. */
const FALLOFF_START = 0.5;
/** visibility > this counts as "seen right now". */
const VISIBLE_THRESHOLD = 0.25;
/** visibility > this marks a cell explored (sticky). */
const EXPLORED_THRESHOLD = 0.15;
/** Minimum sim-seconds between recomputes. */
const RECOMPUTE_INTERVAL = 0.5;
/** Height grid resolution baked once from groundY. */
const HEIGHT_CELLS = 192;

/** Fallback day ranges (world units) when the data `vision` field is absent. */
const DAY_RANGE: Record<ViewerKind, number> = {
  fighter: 110,
  worker: 85,
  building: 100,
  brazier: 52,
};
/**
 * Night sight as a fraction of day range. Prototype day/night tuning —
 * the UE data model carries a single `vision` value per building, so the
 * night shrink is a fixed ratio. Braziers are light sources (ratio 1):
 * they ARE night vision. Torch range is folded into the fighter cluster
 * (fighter night range always exceeds it) — see the module doc.
 */
const NIGHT_RATIO: Record<ViewerKind, number> = {
  fighter: 30 / 110,
  worker: 24 / 85,
  building: 36 / 100,
  brazier: 1,
};
/**
 * Observer-grid cell size (world units). Units stamp into these cells;
 * one viewshed is cast per occupied cell per (team, unit kind).
 */
const CLUSTER_CELL = 32;
/**
 * A cluster's own cell never occludes its sight: half-diagonal + margin.
 */
const CLUSTER_CLEAR = (CLUSTER_CELL * Math.SQRT1_2) / 2 + 1;
/**
 * Jitter-seed base for cluster viewsheds. Building viewers use dense
 * seeds 0..N-1 (caller keeps their order stable); cluster seeds key off
 * observer-cell coordinates so stamp order never matters.
 */
const CLUSTER_SEED_BASE = 1 << 20;

const TAU = Math.PI * 2;

/** Shared sin/cos tables: one trig pair per ray angle, not per viewer. */
const RAY_COS = new Float64Array(RAYS);
const RAY_SIN = new Float64Array(RAYS);
for (let r = 0; r < RAYS; r++) {
  RAY_COS[r] = Math.cos((r / RAYS) * TAU);
  RAY_SIN[r] = Math.sin((r / RAYS) * TAU);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Deterministic [0,1) hash of two integers. No Math.random anywhere in
 * this module; the salt keeps (0,0) from hashing to 0.
 */
function hash01(x: number, y: number): number {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + 1013904223) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const B64_CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function base64Encode(data: Uint8Array): string {
  let out = "";
  for (let i = 0; i < data.length; i += 3) {
    const a = data[i];
    const b = i + 1 < data.length ? data[i + 1] : 0;
    const c = i + 2 < data.length ? data[i + 2] : 0;
    const n = (a << 16) | (b << 8) | c;
    out += B64_CHARS[(n >>> 18) & 63];
    out += B64_CHARS[(n >>> 12) & 63];
    out += i + 1 < data.length ? B64_CHARS[(n >>> 6) & 63] : "=";
    out += i + 2 < data.length ? B64_CHARS[n & 63] : "=";
  }
  return out;
}

function base64Decode(s: string): Uint8Array {
  const clean = s.replace(/[^A-Za-z0-9+/=]/g, "");
  const out: number[] = [];
  for (let i = 0; i < clean.length; i += 4) {
    const v = (ch: string): number => {
      if (ch === "=") return 0;
      const idx = B64_CHARS.indexOf(ch);
      if (idx < 0) throw new Error("vision: bad base64");
      return idx;
    };
    const n =
      (v(clean[i]) << 18) |
      (v(clean[i + 1]) << 12) |
      (v(clean[i + 2]) << 6) |
      v(clean[i + 3]);
    out.push((n >>> 16) & 255);
    if (clean[i + 2] !== "=") out.push((n >>> 8) & 255);
    if (clean[i + 3] !== "=") out.push(n & 255);
  }
  return Uint8Array.from(out);
}

export class VisionSystem {
  readonly cells: number;
  private readonly worldSize: number;
  private readonly half: number;
  private readonly cellSize: number;
  /** Baked heightfield, HEIGHT_CELLS x HEIGHT_CELLS, bilinear-sampled. */
  private readonly heightGrid: Float32Array;
  /** Forest density, cells x cells, rebuilt by setTreeNodes. */
  private readonly density: Float32Array;
  /**
   * Building occluder tops (ground + data `occluder_height`), cells x
   * cells, rebuilt by setOccluders. 0 = no occluder. Sampled in the march
   * as a narrow steep hill inside the maximum-slope computation.
   */
  private readonly occGrid: Float32Array;
  /** True when at least one occluding timber node is present. */
  private forestActive = false;
  /**
   * Coarse forest-presence mask. A set bit means "a timber splat may reach
   * this cell" — the ray then does the exact bilinear density sample.
   * Unset bits provably contribute 0 (outside every splat radius), so the
   * sample is skipped with zero change in results.
   */
  private readonly forestMask = new Uint8Array(
    FOREST_MASK_CELLS * FOREST_MASK_CELLS,
  );
  /** Current visibility per team, 0..1. */
  private readonly vis: [Float32Array, Float32Array];
  /** Sticky explored memory per team. */
  private readonly explored: [Uint8Array, Uint8Array];
  /**
   * Coarse observer grid: one CLUSTER_CELL world-unit cell map per
   * (team, unit kind), indexed team*2+kindOrd (fighter 0, worker 1).
   * Units stamp O(1) into these; each occupied cell casts one viewshed
   * from its stamped centroid. clusterN = ceil(worldSize/CLUSTER_CELL).
   */
  private readonly clusterN: number;
  private readonly obsCount: Uint32Array[];
  private readonly obsSumX: Float64Array[];
  private readonly obsSumZ: Float64Array[];
  /** Last-recompute stats for debugStats(). */
  private lastStats = { clusterViewsheds: 0, buildingViewsheds: 0, stampedUnits: 0 };
  private lastUpdate = -Infinity;

  constructor(opts: {
    groundY: (x: number, z: number) => number;
    worldSize?: number;
    cells?: number;
  }) {
    this.worldSize = opts.worldSize ?? 400;
    this.cells = opts.cells ?? 128;
    this.half = this.worldSize / 2;
    this.cellSize = this.worldSize / this.cells;
    // Bake the heightfield once: groundY may be expensive noise.
    const n = HEIGHT_CELLS;
    this.heightGrid = new Float32Array(n * n);
    for (let iz = 0; iz < n; iz++) {
      const z = -this.half + (iz / (n - 1)) * this.worldSize;
      for (let ix = 0; ix < n; ix++) {
        const x = -this.half + (ix / (n - 1)) * this.worldSize;
        this.heightGrid[iz * n + ix] = opts.groundY(x, z);
      }
    }
    const c = this.cells;
    this.density = new Float32Array(c * c);
    this.occGrid = new Float32Array(c * c);
    this.vis = [new Float32Array(c * c), new Float32Array(c * c)];
    this.explored = [new Uint8Array(c * c), new Uint8Array(c * c)];
    this.clusterN = Math.ceil(this.worldSize / CLUSTER_CELL);
    const nc = this.clusterN * this.clusterN;
    this.obsCount = [
      new Uint32Array(nc),
      new Uint32Array(nc),
      new Uint32Array(nc),
      new Uint32Array(nc),
    ];
    this.obsSumX = [
      new Float64Array(nc),
      new Float64Array(nc),
      new Float64Array(nc),
      new Float64Array(nc),
    ];
    this.obsSumZ = [
      new Float64Array(nc),
      new Float64Array(nc),
      new Float64Array(nc),
      new Float64Array(nc),
    ];
  }

  /**
   * Stamp one unit's position into its faction's observer grid. This is
   * the ONLY per-unit vision work: O(1) — a cell hash plus three array
   * writes. Call only after `isDue(simNow)` reports true, then `update()`.
   * Stamps are single-use: `update()` consumes and clears them whether or
   * not a recompute was due.
   */
  stampUnit(team: VisionTeam, kind: UnitKind, x: number, z: number): void {
    const n = this.clusterN;
    let cx = Math.floor(((x + this.half) / this.worldSize) * n);
    let cz = Math.floor(((z + this.half) / this.worldSize) * n);
    if (cx < 0) cx = 0;
    else if (cx >= n) cx = n - 1;
    if (cz < 0) cz = 0;
    else if (cz >= n) cz = n - 1;
    const g = team * 2 + (kind === "fighter" ? 0 : 1);
    const i = cz * n + cx;
    this.obsCount[g][i]++;
    this.obsSumX[g][i] += x;
    this.obsSumZ[g][i] += z;
  }

  /** True when a visibility recompute is due — gate stamping on this. */
  isDue(simNow: number): boolean {
    return (
      simNow < this.lastUpdate ||
      simNow - this.lastUpdate >= RECOMPUTE_INTERVAL
    );
  }

  /** Stats from the last recompute (viewshed counts, stamped units). */
  debugStats(): { clusterViewsheds: number; buildingViewsheds: number; stampedUnits: number } {
    return { ...this.lastStats };
  }

  private clearStamps(): void {
    for (let g = 0; g < 4; g++) {
      this.obsCount[g].fill(0);
      this.obsSumX[g].fill(0);
      this.obsSumZ[g].fill(0);
    }
  }

  /**
   * Timber nodes feed forest-density occlusion. Call when nodes change.
   * Each timber node with amount > 0 splats a weight-1 gaussian
   * (sigma FOREST_SIGMA) onto the density grid; stone nodes and depleted
   * nodes contribute nothing.
   */
  setTreeNodes(
    nodes: Array<{ x: number; z: number; amount: number; kind: string }>,
  ): void {
    this.density.fill(0);
    this.forestActive = false;
    this.forestMask.fill(0);
    const c = this.cells;
    const radius = FOREST_SPLAT_RADIUS;
    const cellR = Math.ceil(radius / this.cellSize);
    const twoSigmaSq = 2 * FOREST_SIGMA * FOREST_SIGMA;
    // Mask mark radius: splat radius + mask cell half-diagonal, so every
    // density-grid cell with nonzero density sits in a marked mask cell
    // (over-marking is safe; under-marking would be a bug).
    const maskCell = this.worldSize / FOREST_MASK_CELLS;
    const maskRadius = radius + maskCell * Math.SQRT1_2;
    const markR = Math.ceil(maskRadius / maskCell);
    for (const node of nodes) {
      if (node.kind !== "timber" || node.amount <= 0) continue;
      this.forestActive = true;
      const mcx = Math.floor(((node.x + this.half) / this.worldSize) * FOREST_MASK_CELLS);
      const mcz = Math.floor(((node.z + this.half) / this.worldSize) * FOREST_MASK_CELLS);
      for (let mz = mcz - markR; mz <= mcz + markR; mz++) {
        if (mz < 0 || mz >= FOREST_MASK_CELLS) continue;
        for (let mx = mcx - markR; mx <= mcx + markR; mx++) {
          if (mx < 0 || mx >= FOREST_MASK_CELLS) continue;
          const wx = -this.half + (mx + 0.5) * maskCell;
          const wz = -this.half + (mz + 0.5) * maskCell;
          const ddx = wx - node.x;
          const ddz = wz - node.z;
          if (ddx * ddx + ddz * ddz <= maskRadius * maskRadius) {
            this.forestMask[mz * FOREST_MASK_CELLS + mx] = 1;
          }
        }
      }
      const cx = Math.floor(((node.x + this.half) / this.worldSize) * c);
      const cz = Math.floor(((node.z + this.half) / this.worldSize) * c);
      for (let dz = -cellR; dz <= cellR; dz++) {
        const iz = cz + dz;
        if (iz < 0 || iz >= c) continue;
        for (let dx = -cellR; dx <= cellR; dx++) {
          const ix = cx + dx;
          if (ix < 0 || ix >= c) continue;
          const wx = -this.half + (ix + 0.5) * this.cellSize;
          const wz = -this.half + (iz + 0.5) * this.cellSize;
          const d2 =
            (wx - node.x) * (wx - node.x) + (wz - node.z) * (wz - node.z);
          if (d2 > radius * radius) continue;
          this.density[iz * c + ix] += Math.exp(-d2 / twoSigmaSq);
        }
      }
    }
  }

  /**
   * Rasterize building occluders into the occlusion grid. Call when the
   * set of active buildings carrying the UE data `occluder_height` field
   * changes. Each entry's `top` is the absolute world height of the
   * occluder's top (ground + occluder_height); `halfExtent` is the
   * footprint half-diagonal. Cells keep the max top (overlapping
   * buildings don't double-block). In the march the grid is sampled as a
   * narrow steep hill inside the maximum-slope computation.
   */
  setOccluders(
    items: Array<{ x: number; z: number; halfExtent: number; top: number }>,
  ): void {
    this.occGrid.fill(0);
    const c = this.cells;
    const toCell = c / this.worldSize;
    for (const it of items) {
      const x0 = Math.max(
        0,
        Math.floor((it.x - it.halfExtent + this.half) * toCell),
      );
      const x1 = Math.min(
        c - 1,
        Math.ceil((it.x + it.halfExtent + this.half) * toCell),
      );
      const z0 = Math.max(
        0,
        Math.floor((it.z - it.halfExtent + this.half) * toCell),
      );
      const z1 = Math.min(
        c - 1,
        Math.ceil((it.z + it.halfExtent + this.half) * toCell),
      );
      for (let cz = z0; cz <= z1; cz++) {
        const row = cz * c;
        for (let cx = x0; cx <= x1; cx++) {
          const idx = row + cx;
          if (it.top > this.occGrid[idx]) this.occGrid[idx] = it.top;
        }
      }
    }
  }

  /**
   * Recompute visibility; internally throttled to at most every 0.5s of
   * simNow. Consumes the unit stamps (cleared whether or not a recompute
   * was due — only stamp when `isDue(simNow)` is true). `buildings` are
   * the individual data-driven building/brazier viewers (few); unit
   * viewsheds come from the stamped observer grids, one per occupied
   * cell per (team, unit kind), cast from the stamped centroid. Explored
   * memory only ever grows (until reset/restore).
   */
  update(
    buildings: VisionViewer[],
    nightFactor: number,
    simNow: number,
  ): void {
    if (!this.isDue(simNow)) {
      this.clearStamps();
      return;
    }
    this.lastUpdate = simNow;
    this.vis[0].fill(0);
    this.vis[1].fill(0);
    const night = clamp(nightFactor, 0, 1);
    // Buildings first: dense jitter seeds 0..N-1, caller keeps the array
    // order stable so per-ray ranges never shimmer.
    for (let i = 0; i < buildings.length; i++) {
      this.castViewer(buildings[i], i, night);
    }
    // Unit clusters: one viewshed per occupied observer cell, seeded by
    // cell coordinates (stamp order is irrelevant).
    let clusterViewsheds = 0;
    let stampedUnits = 0;
    const n = this.clusterN;
    for (let g = 0; g < 4; g++) {
      const cnt = this.obsCount[g];
      const sx = this.obsSumX[g];
      const sz = this.obsSumZ[g];
      const team = (g >> 1) as VisionTeam;
      const kind: UnitKind = (g & 1) === 0 ? "fighter" : "worker";
      for (let cz = 0; cz < n; cz++) {
        for (let cx = 0; cx < n; cx++) {
          const i = cz * n + cx;
          const c = cnt[i];
          if (c === 0) continue;
          stampedUnits += c;
          clusterViewsheds++;
          this.castViewer(
            {
              x: sx[i] / c,
              z: sz[i] / c,
              team,
              kind,
              clearRadius: CLUSTER_CLEAR,
            },
            CLUSTER_SEED_BASE + g * 4096 + cz * 64 + cx,
            night,
          );
        }
      }
    }
    this.clearStamps();
    this.lastStats = {
      clusterViewsheds,
      buildingViewsheds: buildings.length,
      stampedUnits,
    };
    for (let t = 0 as VisionTeam; t < 2; t = (t + 1) as VisionTeam) {
      const v = this.vis[t];
      const e = this.explored[t];
      for (let i = 0; i < v.length; i++) {
        if (v[i] > EXPLORED_THRESHOLD) e[i] = 1;
      }
    }
  }

  /** 0..1, bilinearly interpolated. Points outside the world clamp. */
  visibilityAt(team: VisionTeam, x: number, z: number): number {
    return this.sampleGrid(this.vis[team], x, z);
  }

  /**
   * visibility > 0.25 — THE API the AI uses to scout honestly. Reports
   * only what the team's viewers can currently see.
   */
  isVisible(team: VisionTeam, x: number, z: number): boolean {
    return this.visibilityAt(team, x, z) > VISIBLE_THRESHOLD;
  }

  exploredAt(team: VisionTeam, x: number, z: number): boolean {
    const c = this.cells;
    const ix = clamp(
      Math.floor(((x + this.half) / this.worldSize) * c),
      0,
      c - 1,
    );
    const iz = clamp(
      Math.floor(((z + this.half) / this.worldSize) * c),
      0,
      c - 1,
    );
    return this.explored[team][iz * c + ix] === 1;
  }

  // ---- faction/grid-level API for honest AI scouting ----
  // The AI reasons about the shared per-faction grids directly: "is this
  // cell visible to / explored by faction X". Same data the renderer and
  // picking use — no privileged per-unit information.

  /** World position -> visibility-grid cell (clamped). */
  worldToCell(x: number, z: number): { cx: number; cz: number } {
    const c = this.cells;
    return {
      cx: clamp(Math.floor(((x + this.half) / this.worldSize) * c), 0, c - 1),
      cz: clamp(Math.floor(((z + this.half) / this.worldSize) * c), 0, c - 1),
    };
  }

  /** Visibility-grid cell -> world position of the cell center. */
  cellToWorld(cx: number, cz: number): { x: number; z: number } {
    return {
      x: -this.half + (cx + 0.5) * this.cellSize,
      z: -this.half + (cz + 0.5) * this.cellSize,
    };
  }

  /** Raw cell query, no interpolation: is this cell currently visible? */
  isCellVisible(team: VisionTeam, cx: number, cz: number): boolean {
    if (cx < 0 || cz < 0 || cx >= this.cells || cz >= this.cells) return false;
    return this.vis[team][cz * this.cells + cx] > VISIBLE_THRESHOLD;
  }

  /** Raw cell query, no interpolation: has this cell ever been explored? */
  isCellExplored(team: VisionTeam, cx: number, cz: number): boolean {
    if (cx < 0 || cz < 0 || cx >= this.cells || cz >= this.cells) return false;
    return this.explored[team][cz * this.cells + cx] === 1;
  }

  /**
   * Live read-only view of a faction's explored grid (cells*cells).
   * For AI scouting scans (find unexplored regions to send scouts).
   * Do not mutate.
   */
  exploredGrid(team: VisionTeam): Uint8Array {
    return this.explored[team];
  }

  /**
   * Live read-only view of a faction's visibility grid (cells*cells,
   * 0..1). Do not mutate.
   */
  visibilityGrid(team: VisionTeam): Float32Array {
    return this.vis[team];
  }

  /**
   * RGBA cells*cells: R=team0 visibility, G=team0 explored,
   * B=team1 visibility, A=team1 explored.
   */
  textureData(): Uint8Array {
    const n = this.cells * this.cells;
    const out = new Uint8Array(n * 4);
    for (let i = 0; i < n; i++) {
      out[i * 4] = Math.round(clamp(this.vis[0][i], 0, 1) * 255);
      out[i * 4 + 1] = this.explored[0][i] ? 255 : 0;
      out[i * 4 + 2] = Math.round(clamp(this.vis[1][i], 0, 1) * 255);
      out[i * 4 + 3] = this.explored[1][i] ? 255 : 0;
    }
    return out;
  }

  /** Base64 explored bits, both teams (team0 bits then team1 bits). */
  serialize(): string {
    const n = this.cells * this.cells;
    const bytes = Math.ceil(n / 8);
    const out = new Uint8Array(bytes * 2);
    for (let t = 0; t < 2; t++) {
      const e = this.explored[t];
      const base = t * bytes;
      for (let i = 0; i < n; i++) {
        if (e[i]) out[base + (i >> 3)] |= 1 << (i & 7);
      }
    }
    return "V1:" + base64Encode(out);
  }

  restore(s: string): void {
    if (!s.startsWith("V1:")) throw new Error("vision: bad save data");
    const raw = base64Decode(s.slice(3));
    const n = this.cells * this.cells;
    const bytes = Math.ceil(n / 8);
    if (raw.length !== bytes * 2) throw new Error("vision: bad save data");
    for (let t = 0; t < 2; t++) {
      const e = this.explored[t];
      e.fill(0);
      const base = t * bytes;
      for (let i = 0; i < n; i++) {
        if (raw[base + (i >> 3)] & (1 << (i & 7))) e[i] = 1;
      }
    }
    this.vis[0].fill(0);
    this.vis[1].fill(0);
    this.lastUpdate = -Infinity;
  }

  reset(): void {
    this.vis[0].fill(0);
    this.vis[1].fill(0);
    this.explored[0].fill(0);
    this.explored[1].fill(0);
    this.density.fill(0);
    this.occGrid.fill(0);
    this.forestActive = false;
    this.forestMask.fill(0);
    this.clearStamps();
    this.lastStats = { clusterViewsheds: 0, buildingViewsheds: 0, stampedUnits: 0 };
    this.lastUpdate = -Infinity;
  }

  // ---- internals ----

  private sampleHeight(x: number, z: number): number {
    const n = HEIGHT_CELLS;
    const fx = clamp(((x + this.half) / this.worldSize) * (n - 1), 0, n - 1);
    const fz = clamp(((z + this.half) / this.worldSize) * (n - 1), 0, n - 1);
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const tx = fx - ix;
    const tz = fz - iz;
    const ix1 = Math.min(ix + 1, n - 1);
    const iz1 = Math.min(iz + 1, n - 1);
    const g = this.heightGrid;
    const a = g[iz * n + ix];
    const b = g[iz * n + ix1];
    const c0 = g[iz1 * n + ix];
    const d = g[iz1 * n + ix1];
    return a + (b - a) * tx + (c0 - a) * tz + (a - b - c0 + d) * tx * tz;
  }

  private sampleGrid(grid: Float32Array, x: number, z: number): number {
    const c = this.cells;
    // Grid coordinates with cell centers at (ix+0.5).
    const fx = clamp(((x + this.half) / this.worldSize) * c - 0.5, 0, c - 1);
    const fz = clamp(((z + this.half) / this.worldSize) * c - 0.5, 0, c - 1);
    const ix = Math.floor(fx);
    const iz = Math.floor(fz);
    const tx = fx - ix;
    const tz = fz - iz;
    const ix1 = Math.min(ix + 1, c - 1);
    const iz1 = Math.min(iz + 1, c - 1);
    const a = grid[iz * c + ix];
    const b = grid[iz * c + ix1];
    const c0 = grid[iz1 * c + ix];
    const d = grid[iz1 * c + ix1];
    return a + (b - a) * tx + (c0 - a) * tz + (a - b - c0 + d) * tx * tz;
  }

  /**
   * Hot loop: rays are marched here with fully inlined bilinear sampling
   * (no method-call overhead per step). Semantics are identical to the
   * documented rules: maximum-slope hill occlusion (blocked steps are
   * skipped but the march continues past the horizon), forest
   * accumulation with soft dimming then a hard block, smoothstep range
   * falloff, stable per-ray jitter.
   *
   * Two semantics-preserving shortcuts: density sampling is skipped when
   * no timber nodes exist, and the ray ends once visibility provably
   * drops below the write threshold (visibility only decreases with
   * distance, so nothing observable is lost).
   */
  private castViewer(
    v: VisionViewer,
    jitterSeed: number,
    nightFactor: number,
  ): void {
    const eyeY = this.sampleHeight(v.x, v.z) + (v.eyeHeight ?? EYE_HEIGHT);
    // Day range comes from the UE data `vision` field when the caller
    // provides it (all building defs carry it); the kind table is the
    // fallback. Night range is the prototype fixed-ratio shrink — the
    // data model carries a single `vision` value. Light sources (torch,
    // brazier) keep ratio 1: they ARE night vision.
    const day = v.rangeDay ?? DAY_RANGE[v.kind];
    let range = day + (day * NIGHT_RATIO[v.kind] - day) * nightFactor;
    // Elevation bonus: high ground sees farther, up to +50%.
    range *= 1 + clamp((eyeY - 2) * 0.012, 0, 0.5);

    const grid = this.vis[v.team];
    const c = this.cells;
    const half = this.half;
    const toCell = c / this.worldSize;
    const hg = this.heightGrid;
    const HN = HEIGHT_CELLS;
    const HN1 = HN - 1;
    const hScale = HN1 / this.worldSize; // height-grid units per world unit
    const dens = this.density;
    const hasForest = this.forestActive;
    const fmask = this.forestMask;
    const maskScale = FOREST_MASK_CELLS / this.worldSize;
    const occ = this.occGrid;
    const clearRadius = v.clearRadius ?? 0;
    const eyeMinus = eyeY + HILL_TOLERANCE; // hoisted (h - eyeY - tol)
    const eyeTarget = eyeY - TARGET_HEIGHT; // hoisted (h + 1 - eyeY)
    const invForestBlock = 1 / FOREST_BLOCK;
    const vx = v.x;
    const vz = v.z;

    // The viewer always sees its own cell: rays start at MARCH_STEP, so
    // the march itself never writes the origin cell.
    {
      let ocx = (((vx + half) * toCell) | 0);
      if (ocx < 0) ocx = 0;
      else if (ocx > c - 1) ocx = c - 1;
      let ocz = (((vz + half) * toCell) | 0);
      if (ocz < 0) ocz = 0;
      else if (ocz > c - 1) ocz = c - 1;
      grid[ocz * c + ocx] = 1;
    }

    for (let r = 0; r < RAYS; r++) {
      // Stable per-ray range jitter: no shimmer across recomputes.
      const jitter = 1 - RANGE_JITTER + 2 * RANGE_JITTER * hash01(jitterSeed, r);
      const maxR = range * jitter;
      const invMaxR = 1 / maxR;
      const dx = RAY_COS[r];
      const dz = RAY_SIN[r];
      // Running max of (terrain - eye - tolerance)/dist.
      let maxBlockSlope = -Infinity;
      let forestAccum = 0;
      for (let d = MARCH_STEP; d <= maxR; d += MARCH_STEP) {
        const x = vx + dx * d;
        const z = vz + dz * d;
        if (x < -half || x > half || z < -half || z > half) break;

        // --- inline heightGrid bilinear (x,z known in-bounds) ---
        const fx = (x + half) * hScale;
        const fz = (z + half) * hScale;
        const ix = fx | 0;
        const iz = fz | 0;
        const tx = fx - ix;
        const tz = fz - iz;
        const ix1 = ix < HN1 ? ix + 1 : HN1;
        const iz1 = iz < HN1 ? iz + 1 : HN1;
        const hRow = iz * HN;
        const hRow1 = iz1 * HN;
        const h00 = hg[hRow + ix];
        const h10 = hg[hRow + ix1];
        const h01 = hg[hRow1 + ix];
        const h11 = hg[hRow1 + ix1];
        const h =
          h00 + (h10 - h00) * tx + (h01 - h00) * tz + (h00 - h10 - h01 + h11) * tx * tz;

        const invD = 1 / d;
        // Building occluders behave as narrow steep hills: take the max
        // of terrain and occluder-top so the maximum-slope computation
        // below hides the ground directly behind a wall while a hilltop
        // past it stays visible. The viewer's own footprint (clearRadius)
        // never occludes its own sight.
        let hh = h;
        if (d > clearRadius) {
          let ocx = (((x + half) * toCell) | 0);
          if (ocx < 0) ocx = 0;
          else if (ocx > c - 1) ocx = c - 1;
          let ocz = (((z + half) * toCell) | 0);
          if (ocz < 0) ocz = 0;
          else if (ocz > c - 1) ocz = c - 1;
          const ot = occ[ocz * c + ocx];
          if (ot > hh) hh = ot;
        }
        const bs = (hh - eyeMinus) * invD;
        if (bs > maxBlockSlope) maxBlockSlope = bs;
        // Hill-blocked: skip the cell, but keep marching — distant
        // ground past the horizon can still be visible.
        if ((hh - eyeTarget) * invD < maxBlockSlope) continue;

        if (hasForest) {
          // Coarse mask first: unset bits provably contribute 0 density.
          const mx = (((x + half) * maskScale) | 0);
          const mz = (((z + half) * maskScale) | 0);
          if (fmask[mz * FOREST_MASK_CELLS + mx]) {
            // --- inline density bilinear ---
            let gx = (x + half) * toCell - 0.5;
            if (gx < 0) gx = 0;
            let gz = (z + half) * toCell - 0.5;
            if (gz < 0) gz = 0;
            const dix = gx | 0;
            const diz = gz | 0;
            const dtx = gx - dix;
            const dtz = gz - diz;
            const dix1 = dix < c - 1 ? dix + 1 : c - 1;
            const diz1 = diz < c - 1 ? diz + 1 : c - 1;
            const dRow = diz * c;
            const dRow1 = diz1 * c;
            const d00 = dens[dRow + dix];
            const d10 = dens[dRow + dix1];
            const d01 = dens[dRow1 + dix];
            const d11 = dens[dRow1 + dix1];
            forestAccum +=
              (d00 +
                (d10 - d00) * dtx +
                (d01 - d00) * dtz +
                (d00 - d10 - d01 + d11) * dtx * dtz) *
              MARCH_STEP;
            if (forestAccum >= FOREST_BLOCK) break; // forest-blocked: ray dies
          }
        }

        const t = d * invMaxR;
        // Inlined smoothstep(0.5, 1, t): full visibility to half range,
        // then a smooth fade to 0 at max range (no hard ring).
        const u = t <= FALLOFF_START ? 0 : (t - FALLOFF_START) * 2;
        const falloff = 1 - u * u * (3 - 2 * u);
        const visVal = falloff * (1 - forestAccum * invForestBlock);
        // Visibility only decreases with d; below the write threshold
        // nothing further down the ray is observable.
        if (visVal <= 0.004) break;
        const idx = ((((z + half) * toCell) | 0) * c + (((x + half) * toCell) | 0));
        if (visVal > grid[idx]) grid[idx] = visVal;
      }
    }
  }
}
